import { useEffect, useRef, useState } from "react";
import GlobalSpinner from "@common/GlobalSpinner";
import { useLocation, useNavigate } from "react-router-dom";
import { PrimaryButton } from "@common/Buttons";
import ScrollContainer from "react-indiana-drag-scroll";
import canvasDatagrid from "canvas-datagrid";
import { useErrorToast } from "@hooks/useErrorToast";
import {
  arraysToObjects,
  downloadBlobAsFile,
  objectsToArrays,
} from "@store/utilities";
import { setupGridContextMenu } from "@utils/setupContextMenu";
import { useSaveAndGenerateMutation } from "@store/file";
import FindReplaceModal from "./FindReplaceModal";
import { getCorrectedDateFormatKeys } from "@utils/helpers";
import { useFetchProductsQuery } from "@store/master";
import { COLUMN_NAME } from "../data/columnNames";

export default function Preview() {
  const [sheetData, setSheetData] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);
  const [allSheetData, setAllSheetData] = useState({});
  const [loading, setLoading] = useState(false);
  const { state, search } = useLocation();
  const [activeSheet, setActiveSheet] = useState("");
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  const gridRef = useRef();
  const navigate = useNavigate();
  const [saveAndGenerate, { error, isLoading }] = useSaveAndGenerateMutation();
  useErrorToast(error);

  //api call to get the enum values
  //skipping the api call if the product is not in the sectionSelectInputFields
  const { data: products } = useFetchProductsQuery(
    { all: true },
    {
      skip:
        !state.previewSelectInputFields ||
        !state.previewSelectInputFields.includes(COLUMN_NAME.PRODUCT),
    }
  );

  // reading excel data and converting it to json and storing it in sheetdata
  const readExcelData = (data) => {
    setLoading(true);
    if (state.useDefaultSheetNames) {
      const allSheetData = {};
      const sheetNames = Object.keys(data.cleanedData);

      sheetNames.forEach((key) => {
        allSheetData[key] = arraysToObjects(data.cleanedData[key]);
      });

      setSheetNames(sheetNames);
      setAllSheetData(allSheetData);

      const firstSheet = sheetNames[0];
      setActiveSheet(firstSheet);
      setSheetData(allSheetData[firstSheet]);
    } else {
      const allSheetData = {
        cleanData: getCorrectedDateFormatKeys(arraysToObjects(data.cleanData), [
          "Date",
        ]),
        deletedData: getCorrectedDateFormatKeys(
          arraysToObjects(data.deletedData),
          ["Date"]
        ),
      };

      setSheetNames(["cleanData", "deletedData"]);
      setAllSheetData(allSheetData);
      const firstSheet = "cleanData";
      setActiveSheet(firstSheet);
      setSheetData(allSheetData[firstSheet]);
    }
    setLoading(false);
  };

  //loading or navigating based on state data
  useEffect(() => {
    if (state && state.data) {
      readExcelData(state.data);
    } else {
      navigate("/", { replace: true });
    }
  }, [state]);

  // rendering data in table
  useEffect(() => {
    setLoading(true);
    const table = document.getElementById("table");
    const parent = document.getElementById("parent");
    const { offsetHeight, offsetWidth } = parent;
    const grid = canvasDatagrid({
      parentNode: table,
      showNewRow: true,
      allowMovingSelection: true,
      allowColumnResizeFromCell: true,
      allowFreezingColumns: true,
      allowFreezingRows: true,
      allowRowResizeFromCell: true,
      selectionFollowsActiveCell: true,
      selectionHandleBehavior: "move",
      showPaste: true,
    });
    gridRef.current = grid;

    setupGridContextMenu(grid, (updatedData) => {
      setSheetData(updatedData);

      setAllSheetData((prev) => ({
        ...prev,
        [activeSheet]: updatedData,
      }));
    });

    grid.style.height = `${offsetHeight - 50}px`;
    grid.style.width = offsetWidth + "px";
    grid.data = sheetData;

    setLoading(false);

    // Clean up
    return () => {
      if (table) {
        table.innerHTML = "";
      }
      gridRef.current = null;
    };
  }, [sheetData, activeSheet]);

  // Prevent browser back navigation on horizontal swipes
  // Prevent browser back navigation on horizontal swipes (including iOS)
  useEffect(() => {
    // For mouse wheel (Windows, Mac)
    const preventOverscrollNavigation = (event) => {
      // Only prevent horizontal overscroll at the left edge
      if (
        event.deltaX < 0 && // Negative deltaX means swiping right (which triggers browser back)
        (window.scrollX <= 0 || document.documentElement.scrollLeft <= 0)
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener("wheel", preventOverscrollNavigation, {
      passive: false,
    });

    // For touch devices (Android, iOS, iPadOS)
    let touchStartX = null;
    let touchStartY = null;
    let maybePrevent = false;

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        // Only consider horizontal swipes near the left edge
        // On iOS, scrollX is always 0, so this works
        maybePrevent =
          window.scrollX <= 10 || document.documentElement.scrollLeft <= 10;
      }
    };

    const handleTouchMove = (e) => {
      if (!maybePrevent || e.touches.length !== 1) return;
      const touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;
      const dx = touchCurrentX - touchStartX;
      const dy = Math.abs(touchCurrentY - touchStartY);

      // Only prevent if horizontal swipe is dominant and to the right
      // On iOS Safari, preventDefault on touchmove will block back navigation
      if (dx > 10 && dx > dy) {
        // iOS Safari requires calling preventDefault on touchmove to block swipe-back
        // This works on iOS, Android, and desktop browsers
        e.preventDefault();
      }
    };

    // iOS Safari requires passive: false for preventDefault to work
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("wheel", preventOverscrollNavigation);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  //adding enum to the product column (input type select instead of text) and locking the fields in the previewLockInputFields
  useEffect(() => {
    setLoading(true);
    //if the product is not in the sectionSelectInputFields, then don't add the enum to the product column
    if (state.previewSelectInputFields) {
      state.previewSelectInputFields.forEach((field) => {
        const column = gridRef.current?.schema.find(
          (col) => col.name === field
        );
        if (column) {
          switch (field) {
            case COLUMN_NAME.PRODUCT:
              column.enum = products?.data.map((pro) => [pro.name, pro.name]);
              break;
            default:
              break;
          }
        }
      });
    }

    //if the field is in the previewLockInputFields, then don't allow the user to edit the field
    // if (state.previewLockInputFields) {
    //   state.previewLockInputFields.forEach((field) => {
    //     const column = gridRef.current.schema.find((col) => col.name === field);
    //     if (column) {
    //       column.editable = false;
    //       column.readOnly = true;
    //     }
    //   });
    // }

    setLoading(false);
  }, [
    products,
    state.previewSelectInputFields,
    state.previewLockInputFields,
    activeSheet,
    sheetData,
  ]);

  //handling sheet change
  const handleSheetSelect = (sheetName) => {
    if (!sheetName) return;
    setAllSheetData((prev) => ({
      ...prev,
      [activeSheet]: sheetData,
    }));
    setActiveSheet(sheetName);
    setLoading(true);

    setTimeout(() => {
      setSheetData(allSheetData[sheetName]);
      setLoading(false);
    }, 100);
  };

  const handleCloseFindAndReplaceModal = () => setShowFindReplaceModal(false);

  const handleKeyDown = (event) => {
    if (event.ctrlKey && event.key === "f") {
      event.preventDefault();
      setShowFindReplaceModal(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  //handle saving changes
  const handleSaveAndGenerate = async () => {
    const convertedData = {};
    Object.keys(allSheetData).forEach((sheetName) => {
      convertedData[sheetName] = objectsToArrays(allSheetData[sheetName]);
    });

    const payload = {
      data: {
        rawFileId: state.data.rawFileId || "",
        month: state.data.month || "",
        typeVal: state.data.typeVal || "",
        year: state.data.year ? Number(state.data.year) : "",
        productName: state.data.productName
          ? String(state.data.productName).toLowerCase()
          : "",
      },
      url: state.saveAndGenerateUrls[tab],
    };

    if (state.useDefaultSheetNames) {
      payload.data.cleanedData = convertedData;
    } else {
      payload.data = { ...payload.data, ...convertedData };
    }

    const response = await saveAndGenerate(payload);
    if (response) {
      if (state.fetchProductsWiseData) {
        downloadBlobAsFile(response.data, `${tab}_cleaned_file.xlsx`);
        navigate(`/${state.redirectUrl}?tab=${tab}`, { replace: true });
      } else {
        navigate(`/${state.redirectUrl}?tab=${tab}`, { replace: true });
      }
    }
  };

  return (
    <div
      id="parent"
      className="overflow-y-auto overflow-x-hidden overscroll-none relative h-full rounded-lg animate-fadeIn bg-secondary-main custom-scrollbar"
    >
      {loading ? (
        <GlobalSpinner />
      ) : sheetData.length > 0 ? (
        <div id="table"></div>
      ) : null}

      <div className="flex fixed left-0 bottom-2 justify-between items-center px-2 w-full h-10 md:h-16 md:px-12">
        <ScrollContainer
          horizontal={true}
          className="hidden gap-2 items-center md:flex"
        >
          {sheetNames.map((name) => {
            const isSelected = name === activeSheet;
            return (
              <div
                key={name}
                value={name}
                onClick={() => (isSelected ? null : handleSheetSelect(name))}
                className={`${
                  isSelected
                    ? "text-white cursor-not-allowed bg-primary-tint"
                    : "bg-gray-200 cursor-pointer"
                } px-2 py-0.5 select-none rounded-md whitespace-nowrap text-xs font-medium`}
              >
                {name}
              </div>
            );
          })}
        </ScrollContainer>

        <div className="block md:hidden">
          <select
            onChange={(e) => handleSheetSelect(e.target.value)}
            className="text-xs font-medium"
          >
            {sheetNames.map((name) => {
              const isSelected = name === activeSheet;
              return (
                <option
                  key={name}
                  value={name}
                  className={`${
                    isSelected
                      ? "text-white cursor-not-allowed bg-primary-tint"
                      : "bg-gray-200 cursor-pointer"
                  } px-2 py-0.5 select-none rounded-md whitespace-nowrap text-xs font-medium`}
                >
                  {name}
                </option>
              );
            })}
          </select>
        </div>

        <PrimaryButton loading={isLoading} onClick={handleSaveAndGenerate}>
          Save & Generate
        </PrimaryButton>
      </div>

      {/* fins and replace modal */}
      {showFindReplaceModal ? (
        <FindReplaceModal
          grid={gridRef?.current}
          onClose={handleCloseFindAndReplaceModal}
        />
      ) : null}
    </div>
  );
}
